// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TableCard from "src/components/shared/tables/TableCard/TableCard";
import { EditIcon, TrashIcon } from "src/assets/icons";

// Types
import { PatientType } from "src/services/patients/types";

// Utils
import {
  calculateYearDifference,
  getLocalDate,
} from "src/utils/date/dateHelper";
import { sexTypeEnums } from "src/constants/enums";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { noHoverBackgroundSx } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type ClinicPatientCardType = {
  patient: PatientType;
  isSelectedForViewing: boolean;
  handleCardClick: (
    _: React.MouseEvent<HTMLDivElement, MouseEvent>,
    patient: PatientType
  ) => void;
  handlePatientToEdit: (patient: PatientType) => void;
  handleDeletePatient: (patient: PatientType) => void;
};

const ClinicPatientCard = ({
  patient,
  isSelectedForViewing,
  handlePatientToEdit,
  handleDeletePatient,
  handleCardClick,
}: ClinicPatientCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const theme = useTheme();
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const formattedPatientName = useMemo(
    () => `${patient?.firstName} ${patient?.lastName}`,
    [patient]
  );

  const formattedPatientAge = useMemo(
    () => calculateYearDifference(patient?.dateOfBirth, clinicInfo?.timezone),
    [patient?.dateOfBirth, clinicInfo?.timezone]
  );

  const formattedPatientSex = useMemo(() => {
    const key = patient?.sex?.toUpperCase() as keyof typeof sexTypeEnums;
    return t(sexTypeEnums[key]);
  }, [patient, t]);

  const nextScheduledExamDate = useMemo(
    () =>
      patient?.nextScheduledExamDate
        ? getLocalDate(patient?.nextScheduledExamDate, clinicInfo?.timezone)
        : "N/A",
    [patient?.nextScheduledExamDate, clinicInfo?.timezone]
  );

  return (
    <TableCard
      key={patient.id}
      isSelectedForViewing={isSelectedForViewing}
      isSelectedForDeletion={false}
      onClick={(event) => handleCardClick(event, patient)}
    >
      <Grid container display="flex" alignItems="center">
        <Grid container item xs={12} display="flex" alignItems="center">
          <Grid container item xs={11} display="flex" alignItems="center">
            <Grid item xs={12} sm={3.3}>
              <Tooltip title={`${patient?.firstName} ${patient?.lastName}`}>
                <Typography
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxWidth="fit-content"
                  sx={{
                    fontSize: "14px",
                    paddingRight: "16px",
                  }}
                >
                  {smallerThanSm && (
                    <span style={{ fontWeight: 600 }}>{t("Name")}: </span>
                  )}
                  {formattedPatientName}
                </Typography>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={1.6}>
              <Tooltip title={formattedPatientAge}>
                <Typography
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxWidth="fit-content"
                  sx={{
                    fontSize: "14px",
                    paddingRight: "16px",
                  }}
                >
                  {smallerThanSm && (
                    <span style={{ fontWeight: 600 }}>{t("Age")}: </span>
                  )}
                  {formattedPatientAge}
                </Typography>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={1.7}>
              <Tooltip title={formattedPatientSex}>
                <Typography
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxWidth="fit-content"
                  sx={{
                    fontSize: "14px",
                    paddingRight: "16px",
                  }}
                >
                  {smallerThanSm && (
                    <span style={{ fontWeight: 600 }}>{t("Sex")}: </span>
                  )}
                  {formattedPatientSex}
                </Typography>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={2.5}>
              <Tooltip title={t(patient?.language)}>
                <Typography
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxWidth="fit-content"
                  sx={{
                    fontSize: "14px",
                    paddingRight: "16px",
                  }}
                >
                  {smallerThanSm && (
                    <span style={{ fontWeight: 600 }}>{t("Language")}: </span>
                  )}
                  {t(patient?.language)}
                </Typography>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={2.25}>
              <Tooltip title={nextScheduledExamDate}>
                <Typography
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxWidth="fit-content"
                  sx={{
                    fontSize: "14px",
                    paddingRight: "16px",
                  }}
                >
                  {smallerThanSm && (
                    <span style={{ fontWeight: 600 }}>{t("Scheduled")}: </span>
                  )}
                  {nextScheduledExamDate}
                </Typography>
              </Tooltip>
            </Grid>
          </Grid>

          <Grid item xs={1} display="flex" justifyContent="flex-end">
            <Tooltip title={t("Edit Patient")}>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handlePatientToEdit(patient);
                }}
                sx={{
                  py: 0,
                  height: "24px",
                  color: "inherit",
                  ...noHoverBackgroundSx,
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("Delete Patient")}>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeletePatient(patient);
                }}
                sx={{
                  py: 0,
                  height: "24px",
                  color: "inherit",
                  ...noHoverBackgroundSx,
                }}
              >
                <TrashIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </TableCard>
  );
};

export default ClinicPatientCard;
